import React, { useEffect, useMemo, useState } from 'react';
import EmployeeProfileEditor from './EmployeeProfileEditor';
import SectionPlaceholder from './sections/SectionPlaceholder';
import EmployeeRatingsSection from './sections/ratings/EmployeeRatingsSection';
import EmployeeSelfServiceAssignmentSection from '../../../components/services/service-staff-assignment/EmployeeSelfServiceAssignmentSection';
import { isSelfServiceAssignmentEnabled } from '../../../components/services/service-staff-assignment/employeeSelfServiceAssignmentUtils';
import { useAuth } from '../../../context/AuthContext';
import EmployeeAvatar from '../employee-management/EmployeeAvatar';
import './styles/employee-profile-workspace.css';

const PROFILE_SECTIONS = [
  { id: 'profile', label: 'Profile' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'ratings', label: 'Ratings' },
  { id: 'services', label: 'Services' },
  { id: 'clients', label: 'Clients' },
];

const EmployeeProfile = ({
  employee,
  onSave,
  saving = false,
  canEdit,
  formatDate,
  currentUser,
  selfViewOnly = false,
}) => {
  const { companyData } = useAuth();
  const [activeSection, setActiveSection] = useState(selfViewOnly ? 'services' : 'profile');
  const [draftStatus, setDraftStatus] = useState(employee?.status || 'active');

  useEffect(() => {
    if (!employee) return;
    setDraftStatus(employee.status || 'active');
  }, [employee?.id, employee?.status]);

  const selfServiceEnabled = isSelfServiceAssignmentEnabled(companyData);

  useEffect(() => {
    if (!selfViewOnly) {
      return;
    }

    if (selfServiceEnabled) {
      setActiveSection('services');
    }
  }, [selfViewOnly, selfServiceEnabled, employee?.id]);

  const visibleSections = useMemo(() => {
    if (selfViewOnly) {
      const allowedIds = [];
      if (selfServiceEnabled) {
        allowedIds.push('services');
      }
      return PROFILE_SECTIONS.filter((section) => allowedIds.includes(section.id));
    }

    return PROFILE_SECTIONS;
  }, [selfViewOnly, selfServiceEnabled]);

  const canSelfManageServices =
    currentUser?.uid === employee?.id && selfServiceEnabled;

  const sectionNavButtons = useMemo(
    () =>
      visibleSections.map((section) => {
        const selected = activeSection === section.id;
        return (
          <button
            key={section.id}
            type="button"
            className={`employee-workspace__navItem ${selected ? 'is-active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        );
      }),
    [activeSection, visibleSections]
  );

  if (!employee) {
    return (
      <div className="employee-profile-state">Loading employee...</div>
    );
  }

  if (selfViewOnly && visibleSections.length === 0) {
    return (
      <div className="employee-profile-state">
        No employee self-service options are enabled for your business yet.
      </div>
    );
  }

  const displayName =
    employee.displayName ||
    `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
    'Employee';
  const title = employee.title || employee.position || 'Team Member';

  const contentTitle = displayName;

  const renderSectionContent = () => {
    if (activeSection === 'profile') {
      return (
        <EmployeeProfileEditor
          employee={employee}
          section="profile"
          status={draftStatus}
          onSave={onSave}
          saving={saving}
        />
      );
    }

    if (activeSection === 'schedule') {
      return (
        <EmployeeProfileEditor
          employee={employee}
          section="schedule"
          status={draftStatus}
          onSave={onSave}
          saving={saving}
        />
      );
    }

    if (activeSection === 'ratings') {
      return <EmployeeRatingsSection ratings={employee.ratings || []} />;
    }

    if (activeSection === 'services') {
      return (
        <EmployeeSelfServiceAssignmentSection
          employee={employee}
          canSelfManage={canSelfManageServices}
        />
      );
    }

    return (
      <SectionPlaceholder
        title={PROFILE_SECTIONS.find((section) => section.id === activeSection)?.label || 'Section'}
        description="This section is part of the new employee workspace layout and is reserved for the next pass. Profile and Schedule are fully functional now."
      />
    );
  };

  return (
    <div className="employee-workspace employee-workspace--employeeProfile">
      <div className="employee-workspace__grid">
        <aside className="employee-workspace__sidebar">
          <div className="employee-workspace__summary">
            <EmployeeAvatar
              employee={employee}
              companyData={companyData}
              size="sidebar"
              alt={displayName}
            />
            <h2 className="employee-workspace__name">{displayName}</h2>
            <p className="employee-workspace__title">{title}</p>
            <div
              className={`employee-workspace__statusRow ${
                draftStatus === 'active' ? 'is-active' : 'is-inactive'
              }`}
            >
              <span>Suspend</span>
              <input
                type="checkbox"
                className="employee-workspace__toggleControl employee-workspace__toggleControl--compact"
                checked={draftStatus === 'active'}
                disabled={!canEdit || activeSection !== 'profile' || saving}
                onChange={(event) => setDraftStatus(event.target.checked ? 'active' : 'inactive')}
                aria-label="Employee status"
              />
              <span>Active</span>
            </div>
          </div>

          <nav
            className="employee-workspace__nav employee-workspace__nav--sidebar"
            aria-label="Employee profile sections"
          >
            {sectionNavButtons}
          </nav>
        </aside>

        <section className="employee-workspace__main">
          <div className="employee-workspace__mainHeader employee-workspace__mainHeader--hero">
            <div className="employee-workspace__mainHero">
              <EmployeeAvatar
                employee={employee}
                companyData={companyData}
                size="hero"
                alt={displayName}
                className="employee-workspace__mainHeroAvatarInner"
              />
              <h1 className="employee-workspace__heading">{contentTitle}</h1>
              <p className="employee-workspace__subheading">
                Joined {formatDate(employee.createdAt)}
              </p>
            </div>
          </div>

          <nav
            className="employee-workspace__nav employee-workspace__nav--main"
            aria-label="Employee profile sections"
          >
            {sectionNavButtons}
          </nav>

          <div className="employee-workspace__content">{renderSectionContent()}</div>
        </section>
      </div>
    </div>
  );
};

export default EmployeeProfile;
